import type { QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  Link,
  Outlet,
  useLocation,
} from '@tanstack/react-router';
import {
  Gift,
  Home,
  LogIn,
  LogOut,
  Menu,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { NotificationsBell } from '@/features/notifications/notifications-bell';
import { TokenBalance } from '@/features/rewards/token-balance';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Toaster } from '@/components/ui/sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/auth-context';

export interface WebappRouterContext {
  queryClient: QueryClient;
}

/**
 * Nav items driving both the desktop header pills and the mobile sidebar.
 * `protected: true` means the route is only shown when the user is signed in.
 */
type NavItem = {
  label: string;
  to: '/' | '/pickem' | '/rewards';
  icon: LucideIcon;
  protected?: boolean;
};

const navItems: NavItem[] = [
  { label: 'Home', to: '/', icon: Home },
  { label: 'Fantasy', to: '/pickem', icon: Trophy },
  { label: 'Rewards', to: '/rewards', icon: Gift, protected: true },
];

function DesktopNav() {
  const { isAuthenticated, user, logout } = useAuth();
  const { pathname } = useLocation();

  const visible = navItems.filter((item) => !item.protected || isAuthenticated);

  return (
    <nav className="hidden md:flex items-center gap-1">
      {visible.map((item) => {
        const isActive =
          item.to === '/' ? pathname === '/' : pathname.startsWith(item.to);
        return (
          <Link key={item.to} to={item.to}>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'text-muted-foreground hover:text-foreground',
                isActive && 'text-foreground bg-primary/10 hover:bg-primary/15',
              )}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </Button>
          </Link>
        );
      })}
      <div className="w-px h-6 bg-border/60 mx-2" />
      {isAuthenticated ? (
        <>
          <span className="text-sm text-muted-foreground max-w-[12rem] truncate">
            {user?.displayName}
          </span>
          <Button variant="outline" size="sm" onClick={logout}>
            <LogOut className="h-4 w-4" />
            Log out
          </Button>
        </>
      ) : (
        <Link to="/login">
          <Button size="sm">
            <LogIn className="h-4 w-4" />
            Sign in
          </Button>
        </Link>
      )}
    </nav>
  );
}

/**
 * A sidebar row styled to match the Overwolf companion's sidebar: icon + label,
 * lime-accent fill for the active route, soft hover tint otherwise. Designed
 * to be used inside the mobile Sheet; pass `onNavigate` so the sheet can close
 * after a selection.
 */
function SidebarRow({
  icon: Icon,
  label,
  active,
  variant = 'default',
  onClick,
  children,
}: {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  variant?: 'default' | 'accent';
  onClick?: () => void;
  children?: (inner: ReactNode) => ReactNode;
}) {
  const inner = (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-3 px-3 h-10 rounded-md text-sm font-medium transition-colors text-left',
        'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-foreground/5',
        active &&
          'bg-sidebar-accent text-sidebar-accent-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        variant === 'accent' &&
          !active &&
          'bg-primary/10 text-primary hover:bg-primary/15 hover:text-primary',
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      <span className="truncate">{label}</span>
    </button>
  );
  return children ? children(inner) : inner;
}

function MobileNav() {
  const { isAuthenticated, user, logout } = useAuth();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const visible = navItems.filter((item) => !item.protected || isAuthenticated);

  const initial = (user?.displayName ?? user?.username ?? '?')
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <div className="md:hidden">
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label="Open menu"
            className="h-10 w-10"
          >
            <Menu className="h-5 w-5" />
          </Button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-72 p-0 bg-sidebar text-sidebar-foreground border-l border-border/40 flex flex-col gap-0"
        >
          <SheetHeader className="px-5 pt-5 pb-4 border-b border-border/40">
            <SheetTitle className="text-left font-bold tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent">
              MAINSTAGE
            </SheetTitle>
            {isAuthenticated ? (
              <div className="flex items-center gap-3 pt-3">
                <div className="h-9 w-9 rounded-full bg-gradient-to-br from-primary to-primary/50 text-primary-foreground flex items-center justify-center text-sm font-bold shrink-0">
                  {initial}
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-semibold truncate">
                    {user?.displayName}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {user?.email ?? user?.username}
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-left">
                Sign in to unlock the app area.
              </p>
            )}
          </SheetHeader>

          <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-1">
            {visible.map((item) => {
              const isActive =
                item.to === '/'
                  ? pathname === '/'
                  : pathname.startsWith(item.to);
              return (
                <SidebarRow
                  key={item.to}
                  icon={item.icon}
                  label={item.label}
                  active={isActive}
                >
                  {(inner) => (
                    <Link to={item.to} onClick={close} className="block">
                      {inner}
                    </Link>
                  )}
                </SidebarRow>
              );
            })}
          </nav>

          <div className="px-2 py-3 border-t border-border/40 space-y-1">
            {isAuthenticated ? (
              <SidebarRow
                icon={LogOut}
                label="Log out"
                onClick={() => {
                  logout();
                  close();
                }}
              />
            ) : (
              <SidebarRow
                icon={LogIn}
                label="Sign in with Google"
                variant="accent"
              >
                {(inner) => (
                  <Link to="/login" onClick={close} className="block">
                    {inner}
                  </Link>
                )}
              </SidebarRow>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function Header() {
  return (
    <header
      className="h-14 border-b border-border/60 bg-card/70 backdrop-blur-lg flex items-center justify-between px-4 sm:px-6 sticky top-0 z-40"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <Link
        to="/"
        className="font-bold text-lg tracking-tight bg-gradient-to-br from-foreground to-foreground/70 bg-clip-text text-transparent"
      >
        MAINSTAGE
      </Link>
      <div className="flex items-center gap-1">
        {/* Signed-in reward controls — visible on mobile and desktop. */}
        <TokenBalance />
        <NotificationsBell />
        <DesktopNav />
        <MobileNav />
      </div>
    </header>
  );
}

function RootLayout() {
  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground overflow-x-hidden">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <footer className="border-t border-border/60 px-6 py-5 text-sm text-muted-foreground print:hidden">
        <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-between gap-3">
          <span>Mainstage · Capture the League LLC</span>
          <nav aria-label="Legal" className="flex flex-wrap gap-x-5 gap-y-2 [&>a]:inline-flex [&>a]:min-h-10 [&>a]:items-center">
            <Link to="/terms" className="hover:text-foreground underline underline-offset-4">Terms and Conditions</Link>
            <Link to="/privacy" reloadDocument className="hover:text-foreground underline underline-offset-4">Privacy Policy</Link>
            <Link to="/privacy" reloadDocument hash="sale-and-sharing-of-personal-information" className="hover:text-foreground underline underline-offset-4">
              Do Not Sell or Share My Personal Information
            </Link>
            <a href="mailto:mainstage@flyquest.gg" className="hover:text-foreground underline underline-offset-4">Contact</a>
          </nav>
        </div>
      </footer>
      <Toaster />
    </div>
  );
}

export const Route = createRootRouteWithContext<WebappRouterContext>()({
  component: RootLayout,
});
